import { useState, useEffect, useCallback, useRef } from 'react';
import socket from '../socket';
import {
  GameStatePrivate,
  RoomInfo,
  RoundResult,
  PlayerRoundScore,
} from '@shared/types';

export type AppScreen = 'menu' | 'lobby' | 'game' | 'result';

interface UseGameStateReturn {
  screen: AppScreen;
  setScreen: (s: AppScreen) => void;
  roomInfo: RoomInfo | null;
  gameState: GameStatePrivate | null;
  roundResult: RoundResult | null;
  finalScores: PlayerRoundScore[] | null;
  sessionKey: string;
  isConnected: boolean;
  error: string | null;
  clearError: () => void;
}

export function useGameState(): UseGameStateReturn {
  const [screen, setScreen] = useState<AppScreen>('menu');
  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [gameState, setGameState] = useState<GameStatePrivate | null>(null);
  const [roundResult, setRoundResult] = useState<RoundResult | null>(null);
  const [finalScores, setFinalScores] = useState<PlayerRoundScore[] | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionKeyRef = useRef<string>('');

  // Initialize session key
  if (!sessionKeyRef.current) {
    const stored = localStorage.getItem('okey_session_key');
    if (stored) {
      sessionKeyRef.current = stored;
    } else {
      const key = crypto.randomUUID();
      sessionKeyRef.current = key;
      localStorage.setItem('okey_session_key', key);
    }
  }

  useEffect(() => {
    socket.connect();

    socket.on('connect', () => {
      setIsConnected(true);
      // Attempt reconnect to existing game
      const sk = sessionKeyRef.current;
      if (sk) {
        socket.emit('game:reconnect', { sessionKey: sk }, (res) => {
          if (res.success && res.state) {
            setGameState(res.state);
            setScreen('game');
          }
        });
      }
    });

    socket.on('disconnect', () => setIsConnected(false));

    socket.on('room:state', ({ room }) => {
      setRoomInfo(room);
    });

    socket.on('game:state', ({ state }) => {
      setGameState(state);
      setScreen('game');
    });

    socket.on('game:roundEnd', ({ result }) => {
      setRoundResult(result);
    });

    socket.on('game:end', ({ scores }) => {
      setFinalScores(scores);
      setScreen('result');
    });

    socket.on('game:error', ({ message }) => {
      setError(message);
    });

    socket.on('game:playerReconnected', ({ playerId }) => {
      console.log('Player reconnected:', playerId);
    });

    socket.on('game:playerDisconnected', ({ playerId }) => {
      console.log('Player disconnected:', playerId);
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('room:state');
      socket.off('game:state');
      socket.off('game:roundEnd');
      socket.off('game:end');
      socket.off('game:error');
      socket.off('game:playerReconnected');
      socket.off('game:playerDisconnected');
    };
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    screen,
    setScreen,
    roomInfo,
    gameState,
    roundResult,
    finalScores,
    sessionKey: sessionKeyRef.current,
    isConnected,
    error,
    clearError,
  };
}
